import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Popover,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import React, { useState } from "react";
import {
    filterDisplayableSlateMismatches,
    formatSlateMismatchValue,
    getSlateFieldLabel,
    SlateMismatch,
} from "./slateUtils";

export type SlateStatus = "synced" | "require_sync" | "not_exist" | null;

export const SLATE_STATUS_LABELS: Record<
    Exclude<SlateStatus, null>,
    string
> = {
    synced: "Synced",
    require_sync: "Out of sync",
    not_exist: "Not in Slate",
};

export const SLATE_STATUS_TOOLTIPS: Record<
    Exclude<SlateStatus, null>,
    string
> = {
    synced: "Catalog matches Prime Video Slate for this license window",
    require_sync:
        "Slate values differ from catalog — click for details",
    not_exist: "Title not found in Prime Video Slate for this ALID",
};

function slateChipColor(
    status: Exclude<SlateStatus, null>
): "success" | "warning" | "error" {
    if (status === "synced") return "success";
    if (status === "require_sync") return "warning";
    return "error";
}

function StatusIcon({
    status,
    size = "small",
}: {
    status: Exclude<SlateStatus, null>;
    size?: "small" | "medium";
}) {
    if (status === "synced") {
        return <CheckCircleIcon color="success" fontSize={size} />;
    }
    if (status === "require_sync") {
        return <WarningAmberIcon color="warning" fontSize={size} />;
    }
    return <ErrorIcon color="error" fontSize={size} />;
}

function SlateMismatchPopover({
    anchorEl,
    open,
    onClose,
    status,
    mismatches,
    referenceId,
    title,
    canSync,
    syncing,
    onSyncToSlate,
    submitError,
}: {
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
    status: Exclude<SlateStatus, null>;
    mismatches?: SlateMismatch[];
    referenceId?: string;
    title?: string;
    canSync?: boolean;
    syncing?: boolean;
    onSyncToSlate?: () => void | Promise<void>;
    submitError?: string | null;
}) {
    const displayMismatches = filterDisplayableSlateMismatches(mismatches);
    const hasMismatches = displayMismatches.length > 0;
    const showSyncButton =
        Boolean(canSync && onSyncToSlate) &&
        (status === "not_exist" || status === "require_sync");

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            slotProps={{
                paper: {
                    sx: { maxWidth: 520, p: 2, minWidth: 280 },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 1,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    <StatusIcon status={status} />
                    <Box sx={{ minWidth: 0 }}>
                        {title ? (
                            <Typography variant="subtitle2" noWrap title={title}>
                                {title}
                            </Typography>
                        ) : null}
                        <Typography variant="subtitle2" color="text.secondary">
                            {SLATE_STATUS_LABELS[status]}
                        </Typography>
                    </Box>
                </Box>
                {showSyncButton ? (
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={
                            syncing ? (
                                <CircularProgress size={14} color="inherit" />
                            ) : (
                                <SyncIcon fontSize="small" />
                            )
                        }
                        disabled={syncing}
                        onClick={() => {
                            void onSyncToSlate?.();
                        }}
                        sx={{ flexShrink: 0 }}
                    >
                        {syncing ? "Syncing…" : "Sync to Slate"}
                    </Button>
                ) : null}
            </Box>
            {referenceId ? (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mb: status === "require_sync" && hasMismatches ? 1 : 0 }}
                >
                    {referenceId}
                </Typography>
            ) : null}
            {status === "require_sync" && hasMismatches ? (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Field</TableCell>
                            <TableCell>Catalog</TableCell>
                            <TableCell>Slate</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {displayMismatches.map((mismatch) => (
                            <TableRow key={mismatch.field}>
                                <TableCell sx={{ fontWeight: 500 }}>
                                    {getSlateFieldLabel(mismatch.field)}
                                </TableCell>
                                <TableCell>
                                    {formatSlateMismatchValue(mismatch.expected)}
                                </TableCell>
                                <TableCell>
                                    {formatSlateMismatchValue(mismatch.actual)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            ) : null}
            {submitError ? (
                <Typography variant="body2" color="error" sx={{ mt: 1.5 }}>
                    {submitError}
                </Typography>
            ) : null}
        </Popover>
    );
}

interface SlateIndicatorProps {
    status: SlateStatus;
    variant?: "icon" | "chip";
    showLabel?: boolean;
    size?: "small" | "medium";
    mismatches?: SlateMismatch[];
    referenceId?: string;
    title?: string;
    interactive?: boolean;
    canSync?: boolean;
    syncing?: boolean;
    onSyncToSlate?: () => void | Promise<void>;
    submitError?: string | null;
}

export default function SlateIndicator({
    status,
    variant = "icon",
    showLabel = false,
    size = "small",
    mismatches,
    referenceId,
    title,
    interactive = true,
    canSync = false,
    syncing = false,
    onSyncToSlate,
    submitError = null,
}: SlateIndicatorProps) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const popoverOpen = Boolean(anchorEl);
    // Missing/unknown status → Not in Slate (never show Pending).
    const resolvedStatus: Exclude<SlateStatus, null> = status || "not_exist";
    const canOpenDetails =
        interactive &&
        (resolvedStatus === "require_sync" ||
            resolvedStatus === "not_exist" ||
            resolvedStatus === "synced");

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        if (!canOpenDetails) return;
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        if (syncing) return;
        setAnchorEl(null);
    };

    const label = SLATE_STATUS_LABELS[resolvedStatus];
    const tooltip = canOpenDetails
        ? `${SLATE_STATUS_TOOLTIPS[resolvedStatus]} (click for details)`
        : SLATE_STATUS_TOOLTIPS[resolvedStatus];

    const detailPopover = (
        <SlateMismatchPopover
            anchorEl={anchorEl}
            open={popoverOpen}
            onClose={handleClose}
            status={resolvedStatus}
            mismatches={mismatches}
            referenceId={referenceId}
            title={title}
            canSync={canSync}
            syncing={syncing}
            onSyncToSlate={onSyncToSlate}
            submitError={submitError}
        />
    );

    if (variant === "chip") {
        return (
            <>
                <Tooltip title={tooltip} arrow>
                    <Chip
                        size="small"
                        variant="outlined"
                        color={slateChipColor(resolvedStatus)}
                        icon={<StatusIcon status={resolvedStatus} />}
                        label={showLabel ? label : label.split(" ")[0]}
                        onClick={canOpenDetails ? handleOpen : undefined}
                        sx={{
                            cursor: canOpenDetails ? "pointer" : "default",
                        }}
                    />
                </Tooltip>
                {detailPopover}
            </>
        );
    }

    return (
        <>
            <Tooltip title={tooltip} arrow>
                <Box
                    component="span"
                    onClick={canOpenDetails ? handleOpen : undefined}
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: canOpenDetails ? "pointer" : "default",
                        borderRadius: "50%",
                        p: 0.25,
                        "&:hover": canOpenDetails
                            ? { bgcolor: "action.hover" }
                            : undefined,
                    }}
                >
                    <StatusIcon status={resolvedStatus} size={size} />
                    {resolvedStatus === "require_sync" &&
                    filterDisplayableSlateMismatches(mismatches).length ? (
                        <InfoOutlinedIcon
                            color="warning"
                            sx={{ fontSize: 12, ml: -0.5, mt: -1 }}
                        />
                    ) : null}
                </Box>
            </Tooltip>
            {detailPopover}
        </>
    );
}
